import { isReducedMotionPreferred } from "@helpers/reducedMotion";
import { INVOICE_SCROLL_CONTAINER_ID } from "./invoiceSectionAnchors";

// Exit half of the step transition. The workspace keys the side panel on the
// current step, so the outgoing panel unmounts in the same commit the new one
// mounts — React never gives it a chance to animate away. This snapshots the
// departing panel's DOM and slides the snapshot out (up when advancing, down
// when going back).
const SIDE_PANEL_ATTRIBUTE = "data-invoice-side-panel";

type StepExitDirection = "forward" | "backward";

// The two halves play in sequence, not together: the old fields clear out
// first, then the new ones arrive. .stepAnimated .stepSection
// (SidePanel.module.scss) delays step-slide-in by exactly this duration —
// keep the two in sync.
const EXIT_DISTANCE_PX = 24;
const EXIT_DURATION_MS = 200;
const EXIT_EASING = "cubic-bezier(0.4, 0, 1, 1)";

// React mirrors controlled values into DOM properties, not attributes, so a
// plain cloneNode would show every field's initial value instead of what's
// on screen.
function syncFormControlValues(source: HTMLElement, ghost: HTMLElement): void {
    const selector = "input, textarea, select";
    const sourceControls = source.querySelectorAll(selector);
    const ghostControls = ghost.querySelectorAll(selector);
    sourceControls.forEach((sourceControl, index) => {
        const ghostControl = ghostControls[index];
        if (
            sourceControl instanceof HTMLInputElement &&
            ghostControl instanceof HTMLInputElement
        ) {
            ghostControl.value = sourceControl.value;
            ghostControl.checked = sourceControl.checked;
            return;
        }
        if (
            sourceControl instanceof HTMLTextAreaElement &&
            ghostControl instanceof HTMLTextAreaElement
        ) {
            ghostControl.value = sourceControl.value;
            return;
        }
        if (
            sourceControl instanceof HTMLSelectElement &&
            ghostControl instanceof HTMLSelectElement
        ) {
            ghostControl.selectedIndex = sourceControl.selectedIndex;
        }
    });
}

// Must be called while the departing step is still mounted (i.e. in the same
// handler that switches the step, before the state update commits).
export function playSidePanelStepExit(direction: StepExitDirection): void {
    const panel = document.querySelector(`[${SIDE_PANEL_ATTRIBUTE}]`);
    const scroller = document.getElementById(INVOICE_SCROLL_CONTAINER_ID);
    if (
        !(panel instanceof HTMLElement) ||
        !(scroller instanceof HTMLElement) ||
        !(scroller.parentElement instanceof HTMLElement)
    ) {
        return;
    }
    if (isReducedMotionPreferred()) {
        return;
    }

    const ghost = panel.cloneNode(true);
    if (!(ghost instanceof HTMLElement)) {
        return;
    }
    syncFormControlValues(panel, ghost);
    // The live panel's anchors and hooks must not find the snapshot.
    ghost.removeAttribute(SIDE_PANEL_ATTRIBUTE);
    ghost.removeAttribute("id");
    ghost.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));

    const panelRect = panel.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    // The workspace (the command island's positioning context,
    // position: relative) hosts the ghost so it can stack above the columns
    // but below the island ($zindex-fixed), which keeps floating on top.
    const workspace = scroller.parentElement;
    const workspaceRect = workspace.getBoundingClientRect();

    // Clips to the scroll surface's box so the snapshot cuts off exactly
    // where the live panel did.
    const wrapper = document.createElement("div");
    wrapper.style.position = "absolute";
    wrapper.style.top = `${scrollerRect.top - workspaceRect.top}px`;
    wrapper.style.left = `${scrollerRect.left - workspaceRect.left}px`;
    wrapper.style.width = `${scrollerRect.width}px`;
    wrapper.style.height = `${scrollerRect.height}px`;
    wrapper.style.overflow = "hidden";
    wrapper.style.pointerEvents = "none";
    wrapper.style.zIndex = "2";
    wrapper.setAttribute("aria-hidden", "true");
    wrapper.inert = true;

    ghost.style.position = "absolute";
    ghost.style.top = `${panelRect.top - scrollerRect.top}px`;
    ghost.style.left = `${panelRect.left - scrollerRect.left}px`;
    ghost.style.width = `${panelRect.width}px`;
    ghost.style.height = `${panelRect.height}px`;
    ghost.style.margin = "0";
    ghost.style.willChange = "transform, opacity";

    wrapper.appendChild(ghost);
    workspace.appendChild(wrapper);

    const dy = direction === "forward" ? -EXIT_DISTANCE_PX : EXIT_DISTANCE_PX;
    const exit = ghost.animate(
        [
            { transform: "translateY(0)", opacity: 1 },
            { transform: `translateY(${dy}px)`, opacity: 0 },
        ],
        { duration: EXIT_DURATION_MS, easing: EXIT_EASING, fill: "forwards" },
    );
    exit.onfinish = () => wrapper.remove();
    exit.oncancel = () => wrapper.remove();
}
